<script setup lang="ts">
import IconButton from '~/components/button/IconButton.vue'
import AddressCopyLink from '~/components/link/AddressCopyLink.vue'
import TransactionCopyLink from '~/components/link/TransactionCopyLink.vue'
import LoadingTable from '~/components/loading/LoadingTable.vue'
import NoDataPreview from '~/components/preview/NoDataPreview.vue'
import CustomSuspense from '~/components/suspense/CustomSuspense.vue'
import { TOKEN_TRANSFER_COLUMN } from '~/components/table/tokens-transfer/tokensTransferColumn.const'
import { useCopy } from '~/composables/useCopy'
import { useTableColumn } from '~/composables/useTable'
import { useTokenType } from '~/composables/useTokenType'
import { IMAGE_BASE_URL } from '~/constants/image.constant'
import { routes } from '~/routes'
import { tokenDetailPageTab } from '~/routes/tokens'
import { NftAllTransferModel, NftTransferModel } from '~/types/model/nft.model'
import { AccountNftTransferModel, TokenTransferModel } from '~/types/model/token.model'
import { INftType } from '~/types/token-type.type'
import BcLink from '~/ui/Link/BcLink.vue'
import BcTable from '~/ui/Table/BcTable.vue'
import { type Column } from '~/ui/Table/types'
import { getRouteParam } from '~/utils/route.util'

interface IProps {
  rows: Array<TokenTransferModel | AccountNftTransferModel | NftTransferModel | NftAllTransferModel> | Array<NftTransferModel> | Array<NftAllTransferModel>
  isLoading: boolean
  rowCnt: number
  columns?: Array<Column>
  showAmountUnit?: boolean
  showTokenInfo?: boolean
}

const props = withDefaults(defineProps<IProps>(), {
  rows: () => [],
  columns: () => TOKEN_TRANSFER_COLUMN,
  showAmountUnit: false,
  showTokenInfo: false
})

const { tokenTypeValue, isErc1155 } = useTokenType<INftType>()

const route = useRoute()
const address = getRouteParam('address')

const path = route.fullPath.split('/').filter(elem => elem)
const localStorageKey = path.length > 2 ? `${path[1]}-${path[path.length - 1]}` : path[1]

const hideAge = ref()

if (process.client) {
  hideAge.value = JSON.parse(window.localStorage.getItem(localStorageKey) || 'false')
}

const exceptList = computed(() => {
  const defaultList = []
  if (route.path.includes('nfts') && !isErc1155.value) {
    defaultList.push('amount')
  }
  if (address && !props.showTokenInfo) {
    defaultList.push('tokenInfo')
  }
  return defaultList.concat(hideAge.value ? 'age' : 'unixTimeStamp')
})

const getTokenDetailPage = (row: TokenTransferModel | NftTransferModel | NftAllTransferModel) => {
  if (!tokenTypeValue && !(row instanceof NftTransferModel)) {
    const tokenAddress = props.showTokenInfo ? row.tokenInfo.address : (address || row.tokenInfo.address)
    return routes.getTokensDetailPageWithTab(tokenAddress, tokenDetailPageTab.transfers)
  }
  return routes.getNftDetailPageWithTab(row instanceof NftAllTransferModel ? row.nftType : tokenTypeValue, row.tokenInfo.address, nftDetailPageTab.transfers)
}

const getNftItemDetailPage = (row: NftTransferModel | NftAllTransferModel | AccountNftTransferModel) => {
  if (row instanceof AccountNftTransferModel) {
    return routes.getNftItemDetailPageWithTab(isErc1155.value ? INftType.ERC1155 : INftType.ERC721, row.caInfo.address, row.tokenId, nftItemDetailPageTab.transfers)
  }
  const nftType = row.nftType || tokenTypeValue
  return routes.getNftItemDetailPageWithTab(nftType, row.tokenInfo.address, row.tokenId, nftItemDetailPageTab.transfers)
}

const getNftCollectionPage = (row: NftTransferModel | NftAllTransferModel | AccountNftTransferModel) => {
  if (row instanceof AccountNftTransferModel) {
    return routes.getNftDetailPageWithTab(isErc1155.value ? INftType.ERC1155 : INftType.ERC721, row.caInfo.address, nftDetailPageTab.transfers)
  }
  const nftType = row.nftType || tokenTypeValue
  return routes.getNftDetailPageWithTab(nftType, row.tokenInfo.address, nftDetailPageTab.transfers)
}

const toggle = () => {
  if (process.client) {
    hideAge.value = !hideAge.value
    window.localStorage.setItem(localStorageKey, JSON.stringify(hideAge.value))
  }
}

const { copy } = useCopy()

const { column } = useTableColumn(props.columns, exceptList)
</script>

<template>
  <CustomSuspense
    :is-loading="isLoading"
    :is-empty="isEmpty(rows)"
  >
    <template #loading>
      <LoadingTable
        :row-cnt="rowCnt"
        :cols="column"
      />
    </template>
    <template #empty>
      <NoDataPreview />
    </template>
    <template #default>
      <BcTable
        :cols="column"
        :rows="rows"
        @change-table-data="toggle"
      >
        <template #transactionHash="{ row }: { row: TokenTransferModel | NftTransferModel | NftAllTransferModel }">
          <TransactionCopyLink
            :hash="row.transactionHash"
            class="link"
          />
        </template>
        <template #method="{ row }: { row: TokenTransferModel | NftTransferModel | NftAllTransferModel }">
          <Chip
            v-if="row.method"
            v-tooltip="row.method"
            :value="row.method"
          />
        </template>
        <template #fromAddressInfo="{ row }: { row: TokenTransferModel | NftTransferModel | NftAllTransferModel }">
          <AddressCopyLink
            class="link"
            :address="row.fromAddressInfo.address"
            :type="row.fromAddressInfo.addressType"
            :name="row.fromAddressInfo.name"
            hide-eoa-icon
          />
        </template>
        <template #arrow-right>
          <FontAwesomeIcon
            icon="arrow-right"
            class="group-hover:text-primary"
          />
        </template>
        <template #toAddressInfo="{ row }: { row: TokenTransferModel | NftTransferModel | NftAllTransferModel }">
          <AddressCopyLink
            class="link"
            :address="row.toAddressInfo.address"
            :type="row.toAddressInfo.addressType"
            :name="row.toAddressInfo.name"
            hide-eoa-icon
          />
        </template>
        <template #tokenInfo="{ row }: { row: TokenTransferModel | NftTransferModel | NftAllTransferModel }">
          <div class="flex w-full items-center gap-x-2">
            <BcLink
              :content="row.tokenInfo.name ? `${row.tokenInfo.name}${row.tokenInfo.symbol && ` (${row.tokenInfo.symbol})`}` : row.tokenInfo.address "
              :to="getTokenDetailPage(row)"
              class="w-full min-w-0 grow"
            >
              <template #prefix>
                <img
                  :src="row.tokenInfo.logo"
                  :alt="`${row.tokenInfo.name} symbol image`"
                  class="h-5 w-5 rounded-full"
                >
              </template>
            </BcLink>
          </div>
        </template>
        <template #tokenId="{ row }: { row: TokenTransferModel | NftTransferModel | NftTransferModel }">
          <div v-if="row instanceof NftTransferModel || row instanceof NftAllTransferModel" class="flex items-center gap-x-2">
            <BcLink
              :content="row.item"
              :to="getNftItemDetailPage(row)"
              class="min-w-0"
            >
              <template #prefix>
                <img
                  :src="row.tokenImage || (IMAGE_BASE_URL + '/token/default.png')"
                  :alt="`${row.item} image`"
                  class="h-5 w-5 rounded-full"
                >
              </template>
            </BcLink>
            <IconButton
              icon="copy"
              @click="copy(row.tokenId)"
            />
          </div>
          <span v-else>-</span>
        </template>
        <template
          v-if="showAmountUnit"
          #amount="{ row }: { row: TokenTransferModel | AccountNftTransferModel | NftTransferModel | NftAllTransferModel }"
        >
          <span>{{ row.amount }} {{ row.tokenInfo.symbol }}</span>
        </template>
        <template
          #item="{ row }: { row: TokenTransferModel | AccountNftTransferModel | NftTransferModel | NftAllTransferModel }"
        >
          <div v-if="row instanceof NftTransferModel || row instanceof NftAllTransferModel" class="flex w-full items-center gap-x-2">
            <BcLink
              :content="row.item"
              :to="getNftItemDetailPage(row)"
              class="w-full min-w-0 grow"
            >
              <template #prefix>
                <img
                  :src="row.tokenImage"
                  :alt="`${row.item} symbol image`"
                  class="h-5 w-5 rounded-full"
                >
              </template>
            </BcLink>
            <IconButton
              icon="copy"
              @click="copy(row.tokenId)"
            />
          </div>
          <span v-else>-</span>
        </template>

        <template
          #collection="{ row }: { row: TokenTransferModel | AccountNftTransferModel | NftTransferModel | NftAllTransferModel }"
        >
          <div v-if="row instanceof NftTransferModel || row instanceof NftAllTransferModel" class="flex w-full items-center gap-x-2">
            <BcLink
              :content="row.collection"
              :to="getNftCollectionPage(row)"
              class="w-full min-w-0 grow"
            >
              <template #prefix>
                <img
                  :src="row.tokenInfo.logo"
                  :alt="`${row.collection} symbol image`"
                  class="h-5 w-5 rounded-full"
                >
              </template>
            </BcLink>
            <IconButton
              icon="copy"
              @click="copy(row.tokenInfo.address)"
            />
          </div>
          <span v-else>-</span>
        </template>
      </BcTable>
    </template>
  </CustomSuspense>
</template>

<style scoped lang="scss">
.link {
  max-width: 200px;

  @include over_xl {
    max-width: 270px;
  }
}

.token-id {
  @apply truncate max-w-[100px];
}
</style>
